<script lang="ts" setup>
import { useDebounceFn } from '@vueuse/core';
import {
  computed,
  onMounted,
  ref,
  toRefs,
  watch,
} from 'vue';
import { useI18n } from 'vue-i18n';

import { GrammarWorkflowToast } from '@nora-monorepo/grammar-ui';
import type {
  MessageResultV2DTO,
  QuotedMessageResultDTO,
} from '@nora-monorepo/nora-api-client';
import {
  bgPrimary,
  borderSeparatorNoTransparency,
  useConfigStore,
  useToastStore,
} from '@nora-monorepo/ui-shared';

import ConversationHeader from '#nora-chat-ui/components/open-conversations/ConversationHeader.vue';
import MessageInput from '#nora-chat-ui/components/open-conversations/messages/MessageInput.vue';
import ViewMessage from '#nora-chat-ui/components/open-conversations/messages/ViewMessage.vue';
import ScrollToNewContentButton from '#nora-chat-ui/components/open-conversations/ScrollToNewContentButton.vue';
import type { ConversationState } from '#nora-chat-ui/states';
import { useAttachmentStore } from '#nora-chat-ui/stores/attachment-store';
import { useConversationStore } from '#nora-chat-ui/stores/conversation/conversation-store';
import { useConversationMessageStore } from '#nora-chat-ui/stores/conversation/message/conversation-message-store';
import { useScrollStore } from '#nora-chat-ui/stores/scroll-store';
import { generateThumbnail } from '#nora-chat-ui/utils/generate-video-thumbnail';
import { getExtension } from '#nora-chat-ui/utils/get-extension';
import { getImageDimensions } from '#nora-chat-ui/utils/get-image-height-and-width';

interface ConversationProps {
  conversationState: ConversationState;
}

const props = defineProps<ConversationProps>();

const scrollStore = useScrollStore();
const conversationStore = useConversationStore();
const conversationMessageStore = useConversationMessageStore();
const configStore = useConfigStore();
const toastStore = useToastStore();
const attachmentStore = useAttachmentStore();
const { t } = useI18n();

const { conversationState } = toRefs(props);
const messageListContainer = ref<HTMLElement | null>(null);
const bottomOfList = ref<HTMLElement | null>(null);
const showErrorMessage = ref(false);
const messagesRefs = ref(new Map<string, HTMLElement>());
const stopNextScrollAction = ref(false);
const isHovering = ref(false);
const dragCounter = ref(0);
const container = ref();

let LOADING_MESSAGES = false;

const isEditingThisConversation = computed((): boolean => {
  return conversationMessageStore.isEditingConversation(
    conversationState.value.conversationListResult.id,
  );
});

async function loadInitialMessages(): Promise<boolean> {
  if (LOADING_MESSAGES) {
    return false;
  }

  LOADING_MESSAGES = true;

  try {
    await conversationStore.loadConversationMessages(
      conversationState.value.conversationListResult.id,
    );

    showErrorMessage.value = false;
  }
  catch {
    showErrorMessage.value = true;
    LOADING_MESSAGES = false;

    return false;
  }

  LOADING_MESSAGES = false;

  return true;
}

const onScroll = useDebounceFn(
  () => {
    scrollStore.onScroll(conversationState.value.conversationListResult.id);
    scrollStore.getScrollPositionAtTop(
      conversationState.value.conversationListResult.id,
    );
  },
  100,
  { maxWait: 1000 },
);

watch(
  () => conversationState.value.highlightedMessage,
  (highlightedMessage) => {
    if (highlightedMessage === undefined) {
      return;
    }
    scrollToHighlightedMessageResult(highlightedMessage);
  },
);

watch(
  () =>
    scrollStore.getScrollPositionAtTop(
      conversationState.value.conversationListResult.id,
    ),
  async (scrollPositionIsAtTop) => {
    if (!scrollPositionIsAtTop) {
      return;
    }

    const timestamp = conversationState.value.messages[0]?.message.createdAt;

    const options = timestamp
      ? { timestamp: timestamp.toISOString() }
      : undefined;

    const prevScrollHeight = scrollStore.getConversationScrollHeight(
      conversationState.value.conversationListResult.id,
    );

    if (stopNextScrollAction.value) {
      // Scroll happened because of navigation to message result. Should not load and do any scrolling
      stopNextScrollAction.value = false;
      return;
    }

    await conversationStore.loadConversationMessages(
      conversationState.value.conversationListResult.id,
      options,
    );

    await scrollStore.scrollConversationAfterLoadingMessages(
      conversationState.value.conversationListResult.id,
      prevScrollHeight,
      { behavior: 'instant' },
    );
  },
  {
    immediate: true,
  },
);

onMounted(async () => {
  if (!conversationState.value.initialMessagePageLoaded) {
    conversationState.value.initialMessagePageLoaded = await loadInitialMessages();
  }

  scrollStore.setElementsForConversationId(
    conversationState.value.conversationListResult.id,
    messageListContainer.value,
    bottomOfList.value,
  );

  if (conversationState.value.highlightedMessage) {
    scrollToHighlightedMessageResult(
      conversationState.value.highlightedMessage,
    );
  }
  else {
    scrollStore.scrollConversationToBottom(
      conversationState.value.conversationListResult.id,
      true,
      {
        behavior: 'instant',
      },
    );
  }
});

async function scrollToHighlightedMessageResult(highlightedMessage: MessageResultV2DTO | QuotedMessageResultDTO) {
  const messageElement = messagesRefs.value.get(highlightedMessage.id);

  if (!messageElement) {
    await conversationStore.loadConversationMessages(conversationState.value, {
      timestamp: highlightedMessage.createdAt.toISOString(),
      newer: false,
    });

    await scrollToHighlightedMessageResult(highlightedMessage);

    return;
  }

  stopNextScrollAction.value = true;

  await scrollStore.scrollConversationToMessage(
    conversationState.value.conversationListResult.id,
    messageElement,
    { behavior: 'smooth' },
  );

  stopNextScrollAction.value = false;
}

/**
 * Method called when the user adds an attachment to a draft message
 * @param file
 */
async function onAttachmentAdd(file: File) {
  if (file.type.startsWith('video/')) {
    const preview = await generateThumbnail(file);

    await attachmentStore.uploadFile(
      conversationState.value.conversationListResult.id,
      file,
      preview,
    );
  }
  else {
    const imageDimensions = await getImageDimensions(file);

    const reader = new FileReader();

    reader.onload = async (event) => {
      await attachmentStore.uploadFile(
        conversationState.value.conversationListResult.id,
        file,
        event.target?.result as string,
        0,
        imageDimensions.height,
        imageDimensions.width,
      );
    };

    reader.readAsDataURL(file);
  }
}

function isMimeTypeValid(file: File): boolean {
  if (!configStore.appConfig) {
    throw new Error('No app config loaded');
  }

  if (!file.type) {
    return false;
  }

  const fileMimeType = file.type.toLowerCase();
  const fileMimeTypeParts = fileMimeType.split('/');
  const filePrimaryType = fileMimeTypeParts[0];

  if (configStore.appConfig?.fileApi.allowedMIMETypes.includes('*')) {
    return true;
  }

  for (const allowedType of configStore.appConfig.fileApi.allowedMIMETypes) {
    if (allowedType.toLowerCase() === fileMimeType) {
      return true;
    }

    if (allowedType.endsWith('/*')) {
      const allowedPrimaryType = allowedType.split('/')[0];

      if (filePrimaryType === allowedPrimaryType) {
        return true;
      }
    }
  }

  return false;
}

function handleDrop(event: DragEvent) {
  event.preventDefault();

  isHovering.value = false;
  dragCounter.value = 0;

  if (isEditingThisConversation.value) {
    return;
  }

  if (!event.dataTransfer) {
    return;
  }

  for (const item of Array.from(event.dataTransfer.items)) {
    if (item.kind !== 'file') {
      continue;
    }

    const file = item.getAsFile();

    if (!file) {
      continue;
    }

    const isFileValid = isMimeTypeValid(file);

    if (isFileValid) {
      onAttachmentAdd(file);
    }
    else {
      toastStore.addWarn({
        message: `${t('attachments.invalidFileType')} ${
          getExtension(file.type) || file.type
        }`,
      });
    }
  }
}

function isDraggingFile(event: DragEvent) {
  if (event?.dataTransfer?.items && event.dataTransfer.items.length) {
    return Array.from(event?.dataTransfer?.items).some(
      item => item.kind === 'file',
    );
  }

  return event.dataTransfer?.types.includes('Files');
}

function showOverlay(event: DragEvent) {
  if (!isDraggingFile(event)) {
    return;
  }

  dragCounter.value++;

  if (dragCounter.value > 0) {
    isHovering.value = true;
  }
}

function hideOverlay() {
  dragCounter.value--;

  if (dragCounter.value <= 0) {
    isHovering.value = false;
  }
}

function setRef(element: HTMLElement | unknown, id: string) {
  if (!element || !(element instanceof HTMLElement)) {
    return;
  }

  messagesRefs.value.set(id, element);
}
</script>

<template>
  <div
    ref="container"
    class="chat-input-wrapper row-span-full min-h-0 sm:min-w-0 md:min-w-[360px] flex flex-col first:border-l-0 border-l"
    :class="[borderSeparatorNoTransparency, bgPrimary]"
    @drop="handleDrop"
    @dragover.prevent
    @dragenter.prevent
    @dragenter="showOverlay"
    @dragleave="hideOverlay"
  >
    <div v-if="isHovering" class="overlay">
      {{ isEditingThisConversation ? t('files.dropDisabledWhileEditing') : t('files.drop') }}
    </div>

    <ConversationHeader :conversation-state="conversationState" />

    <GrammarWorkflowToast
      v-if="showErrorMessage"
      variant="error"
      :message="t('errors.loadingMessagesError')"
      :is-open="true"
    />

    <div
      ref="messageListContainer"
      class="grow overflow-y-auto overflow-x-hidden w-full mx-auto"
      @scroll.passive="onScroll"
    >
      <div
        v-for="(messageState, index) in conversationState.messages"
        :key="messageState.message.id"
        :ref="
          (element: HTMLElement | unknown) =>
            setRef(element, messageState.message.id)
        "
        class="max-w-[1024px] w-full mx-auto"
      >
        <ViewMessage
          :message-state="messageState"
          :is-newest="index === conversationState.messages.length - 1"
          :container-ref="messageListContainer"
          :operation-id="conversationState.conversation?.operationId"
        />
      </div>
      <div ref="bottomOfList" />
    </div>

    <ScrollToNewContentButton
      :conversation-id="conversationState.conversationListResult.id"
    />
    <MessageInput
      v-chat-can="{
        permission: 'create:chat.conversation.message',
        conversationState,
      }"
      class="max-w-[1024px] w-full mx-auto"
      :conversation-container="container"
      :conversation-state="conversationState"
      :on-attachment-add="onAttachmentAdd"
    />
  </div>
</template>

<style scoped>
.chat-input-wrapper {
  position: relative;
}

.overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.7);
  color: white;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 2;
  font-size: 20px;
  font-weight: bold;
}
</style>
